import { forwardRef, useId, useRef, useState, type InputHTMLAttributes, type KeyboardEvent } from "react";

import { evaluate, isExpression, type ArithResult } from "@/lib/arith";
import { cx } from "@/lib/cx";

import { Input } from "./Field";

type Options = { scale?: number; allowNegative?: boolean };

/** The number a calculator entry stands for, or the trimmed text when it isn't one (the server then says why). */
export function resolveAmount(text: string, options?: Options): string {
  const r = evaluate(text, options);
  return r.ok ? r.value : text.trim();
}

// Layout classes size the wrapper (which holds the preview), the rest style the input.
const LAYOUT = /^(w-|min-w-|max-w-|flex-|flex$|shrink|grow|basis-|col-span|self-)/;
function split(className?: string): { outer: string; inner: string } {
  const tokens = (className ?? "").split(/\s+/).filter(Boolean);
  const outer = tokens.filter((t) => LAYOUT.test(t));
  return { outer: outer.join(" ") || "w-full", inner: tokens.filter((t) => !LAYOUT.test(t)).join(" ") };
}

function Preview({ id, result, show }: { id: string; result: ArithResult | null; show: boolean }) {
  if (!result || !show) return null;
  return (
    <span
      id={id}
      className={cx(
        "pointer-events-none absolute top-full right-0 z-20 mt-0.5 rounded-sm border px-1.5 py-0.5 text-xs whitespace-nowrap tabular shadow-sm",
        result.ok ? "border-line bg-surface-raised text-fg" : "border-danger/40 bg-danger-subtle text-danger-text",
      )}
    >
      {result.ok ? `${result.exact ? "=" : "≈"} ${result.value}` : result.error}
    </span>
  );
}

type CalcProps = Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type"> &
  Options & {
    value: string;
    /** Every keystroke (the text), and the result once an expression is worked out (on Enter or leaving the cell). */
    onChange: (text: string) => void;
    /** False: Enter goes to `onKeyDown` as usual (cells that commit on Enter). */
    enterSettles?: boolean;
  };

/**
 * A numeric field that is also a calculator: "45*1.2", "2000 x 1.75" or "45 + 20%" shows its result
 * underneath while typing and becomes that number on Enter or blur. The first Enter only works the
 * expression out, so a form isn't submitted with a number the person hasn't seen.
 */
export const CalcInput = forwardRef<HTMLInputElement, CalcProps>(function CalcInput(
  { value, onChange, scale, allowNegative, enterSettles = true, className, onBlur, onKeyDown, onFocus, "aria-describedby": describedBy, "aria-invalid": invalid, ...rest },
  ref,
) {
  const previewId = useId();
  const before = useRef(value);
  const [touched, setTouched] = useState(false);
  const result = value.trim() ? evaluate(value, { scale, allowNegative }) : null;
  const settle = () => {
    setTouched(true);
    if (result?.ok && result.expression) onChange(result.value);
  };
  const { outer, inner } = split(className);
  const failed = touched && result !== null && !result.ok;
  const show = isExpression(value) || failed;
  return (
    <span className={cx("relative inline-block", outer)}>
      <Input
        ref={ref}
        {...rest}
        inputMode="decimal"
        autoComplete="off"
        value={value}
        className={cx("w-full", inner)}
        aria-invalid={invalid || failed || undefined}
        aria-describedby={[describedBy, show ? previewId : null].filter(Boolean).join(" ") || undefined}
        onFocus={(e) => {
          before.current = value;
          onFocus?.(e);
        }}
        onChange={(e) => {
          setTouched(false);
          onChange(e.target.value);
        }}
        onBlur={(e) => {
          settle();
          onBlur?.(e);
        }}
        onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
          if (e.key === "Enter" && !enterSettles) setTouched(true);
          if (e.key === "Enter" && enterSettles && result?.ok && result.expression) {
            e.preventDefault();
            settle();
            return;
          }
          if (e.key === "Escape" && value !== before.current) {
            e.preventDefault();
            e.stopPropagation();
            onChange(before.current);
            return;
          }
          onKeyDown?.(e);
        }}
      />
      <Preview id={previewId} result={result} show={show} />
    </span>
  );
});

type CellProps = Options & {
  value: string;
  onCommit: (value: string) => void;
  label: string;
  disabled?: boolean;
  className?: string;
};

/**
 * A numeric table cell that commits its number on Enter or blur and restores the saved value on
 * Escape. It accepts calculator entries like CalcInput; an entry that doesn't work out is kept on
 * screen, marked invalid and never sent.
 */
export function CalcCell({ value, onCommit, label, disabled, className, scale, allowNegative }: CellProps) {
  const [text, setText] = useState(value);
  const [shown, setShown] = useState(value);
  if (shown !== value) {
    // The value changed on the server (another edit): show it.
    setShown(value);
    setText(value);
  }
  const commit = (raw: string) => {
    const r = evaluate(raw, { scale, allowNegative });
    if (!r.ok) return;
    setText(r.value);
    if (r.value !== value) onCommit(r.value);
  };
  return (
    <CalcInput
      aria-label={label}
      value={text}
      disabled={disabled}
      scale={scale}
      allowNegative={allowNegative}
      enterSettles={false}
      className={className ?? "h-7 w-24 text-right tabular"}
      onChange={setText}
      onBlur={() => text.trim() !== "" && text !== value && commit(text)}
      onKeyDown={(e) => {
        // Leaving the cell commits (once), as it does for a click elsewhere.
        if (e.key === "Enter" && evaluate(text, { scale, allowNegative }).ok) (e.target as HTMLInputElement).blur();
      }}
    />
  );
}
